import React, { useEffect } from 'react';
import { Checkbox, Form, FormInstance, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TermsAndConditions } from './TermsAndConditionsType';
import { useWizard } from '../../../components/wizard/useWizard';
import { useNewApplication } from './useNewApplication';
import {
	Application,
	DraftState,
} from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;

const SectionHeader = ({ heading }: { heading: string }) => {
	const { t, i18n } = useTranslation();
	const isJustifyNeeded = i18n.language !== 'ma' && i18n.language !== 'ta';

	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-xl text-primary-blue my-4">
				{heading}
			</Text>
			<Text
				className={`mb-[8px] ${
					isJustifyNeeded ? 'text-justify' : ''
				} font-sans font-medium text-sm`}
			>
				{t('weExpressOurGratitude')}
			</Text>
			<Text
				className={`mb-[8px] ${
					isJustifyNeeded ? 'text-justify' : ''
				} font-sans font-medium text-sm`}
			>
				{t('shouldYouHaveAnyConcern')}{' '}
				<a href="mailto:concerns.pcms@heartfulness.org">{t('concernsEmail')}</a>
			</Text>
		</div>
	);
};

export const TermsAndConditionStep: React.FC<{ form: FormInstance }> = ({
	form,
}: {
	form: FormInstance;
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex flex-col">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				style={{ maxWidth: 600 }}
				initialValues={{ remember: true }}
				autoComplete="off"
				form={form}
			>
				<SectionHeader heading={t('termsAndConditions')} />
				<Form.Item
					name="iAmAwareThatThisProcessLeadsToTraining"
					valuePropName="checked"
				>
					<Checkbox className="font-sans font-medium text-sm">
						{t('iAmAwareThatThisProcessLeadsToTraining')}
					</Checkbox>
				</Form.Item>
				<Form.Item name="iAgreeToTermsAndConditions" valuePropName="checked">
					<Checkbox className="font-sans font-medium text-sm">
						{`${t('iHaveReadAndAgree')}`}{' '}
						<a
							href="https://heartfulness.org/en/terms/"
							target="_blank"
							rel="noopener noreferrer"
						>
							{t('termsOfUse')}
						</a>{' '}
						{t('and')}{' '}
						<a
							href="https://heartfulness.org/en/privacy-policy/"
							target="_blank"
							rel="noopener noreferrer"
						>
							{t('privacyPolicy')}
						</a>
					</Checkbox>
				</Form.Item>
				<Form.Item name="iConsentToUndertakeTraining" valuePropName="checked">
					<Checkbox className="font-sans font-medium text-sm">
						{t('iConsentToUndertakeTraining')}
					</Checkbox>
				</Form.Item>
				<Form.Item name="iCertifyAllDetailsAreTrue" valuePropName="checked">
					<Checkbox className="font-sans font-medium text-sm">
						{t('iCertifyAllDetailsAreTrue')}
					</Checkbox>
				</Form.Item>
				<Form.Item name="confirmAndSubmit" valuePropName="checked">
					<Checkbox className="font-sans font-medium text-sm">
						{t('confirmAndSubmit')}
					</Checkbox>
				</Form.Item>
			</Form>
		</div>
	);
};

export const TermsAndConditionStepContainer = () => {
	const { t } = useTranslation();
	const [form] = Form.useForm<TermsAndConditions>();
	const { setSubmitButtonDisabled, setSubmitButtonText } = useWizard();
	const values = Form.useWatch<TermsAndConditions>([], form);
	const {
		termsAndConditions,
		setTermsAndConditions,
		applicationDetails,
		setApplicationDetails,
	} = useNewApplication();

	useEffect(() => {
		if (values) {
			const areAllTermsAndConditionsAgreed =
				(values.iAgreeToTermsAndConditions &&
					values.iAmAwareThatThisProcessLeadsToTraining &&
					values.iConsentToUndertakeTraining &&
					values.iCertifyAllDetailsAreTrue &&
					values.confirmAndSubmit) as boolean;
			setSubmitButtonDisabled(!areAllTermsAndConditionsAgreed);
			setTermsAndConditions(values);
			const newAppDetails = {
				...applicationDetails,
				draftState: DraftState.TERMS_AND_CONDITIONS,
			} as Application;
			setApplicationDetails(newAppDetails);
		} else {
			setSubmitButtonDisabled(true);
		}
		setSubmitButtonText(t('submit'));
		// eslint-disable-next-line
	}, [
		setSubmitButtonDisabled,
		setSubmitButtonText,
		setTermsAndConditions,
		values,
		t,
		setApplicationDetails,
	]);

	useEffect(() => {
		form.setFieldsValue(termsAndConditions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	return <TermsAndConditionStep form={form} />;
};
